import {
  concat,
  countBy,
  filter,
  flatten,
  identity,
  isNil,
  lensPath,
  map,
  pathOr,
  pipe,
  pluck,
  prop,
  propEq,
  reduceBy,
  reject,
  view,
} from "ramda";
import type { DefaultRootState } from "react-redux";
import { createSelector } from "reselect";
import { sortRoles } from "helpers/shared/users";

import type { IRole } from "types/user";
import type {
  IAnalyticsUserInfo,
  IAppUser,
  IPOSUserInfo,
  IUserInfo,
} from "types/user/app_user";

export const selectUsers = (state: DefaultRootState) => state.users;
export const selectCreateUserResponse = createSelector(
  selectUsers,
  prop("createUserResponse")
);
export const selectEditableUser = createSelector(
  selectUsers,
  prop("editableUser")
);
export const selectFetchedBrandId = createSelector(
  selectUsers,
  prop("fetchedBrandId")
);

const viewAnalyticsInfo = view<IAppUser, IAnalyticsUserInfo[]>(
  lensPath(["applications", "analytics"])
);
const viewBreadcrumbInfo = view<IAppUser, IPOSUserInfo[]>(
  lensPath(["applications", "breadcrumb"])
);

interface IRoleCount {
  [index: string]: number;
}

const countRoles = pipe<
  IUserInfo[][],
  IUserInfo[],
  string[][],
  string[],
  string[],
  IRoleCount
>(
  flatten as (x: IUserInfo[][]) => IUserInfo[],
  pluck("roles"),
  flatten as (x: string[][]) => string[],
  reject(isNil),
  countBy(identity)
);

const withUserCounts = (roleCount: IRoleCount) =>
  map((role: IRole) => ({
    ...role,
    userCount: pathOr(0, [role.id], roleCount),
  }));

export const selectPincodesByStore = createSelector(
  selectUsers,
  ({ all, posOnly }) => {
    const allPosUsers = pipe<
      IAppUser[],
      IPOSUserInfo[][],
      IPOSUserInfo[][],
      IPOSUserInfo[]
    >(
      map(viewBreadcrumbInfo),
      reject(isNil),
      flatten as (l: IPOSUserInfo[][]) => IPOSUserInfo[]
    )(all);

    return reduceBy<IPOSUserInfo, string[]>(
      (acc, { pincode }) => (pincode ? acc.concat(pincode) : acc),
      [] as string[],
      prop("storeKey"),
      concat(allPosUsers, posOnly)
    );
  }
);

export const selectRequestStates = createSelector(
  selectUsers,
  ({ allUsers, analytics, breadcrumb, inventory }) => ({
    allUsers: allUsers.requestStates,
    analytics: analytics.requestStates,
    breadcrumb: breadcrumb.requestStates,
    inventory: inventory.requestStates,
  })
);

export const selectRoles = createSelector(
  selectUsers,
  ({ analytics, breadcrumb, all, posOnly }) => {
    const analyticsCount = countRoles(map(viewAnalyticsInfo, all));
    const breadcrumbCount = countRoles(
      concat(map(viewBreadcrumbInfo, all), [posOnly])
    );
    return {
      analytics: sortRoles(withUserCounts(analyticsCount)(analytics.roles)),
      breadcrumb: sortRoles(
        withUserCounts(breadcrumbCount)(
          breadcrumb.roles.filter((role) => role.status === 1)
        )
      ),
    };
  }
);

export const selectZones = createSelector(
  selectUsers,
  ({ breadcrumb: { zones } }) => filter(propEq("status", 1), zones)
);
